import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ArrowLeft, Upload, ChevronDown, X, Check, CircleCheck, Trash2 } from 'lucide-react';
import Checkbox from 'react-custom-checkbox';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFuelTypesForFilter, getCarTypesForFilter, getTransmissionsForFilter, getBodyTypesForFilter, getOperatingLocations, createCar, updateCar, uploadCoverImage, uploadSecondaryImage, deletImage, deleteImage, getCarDetail } from '../../api';
import { Loader } from '../../Components/Loader';
import { ToastMsg } from '../../Components/ToastMsg';



const tags = ['Luxury Car', 'Popular Car', 'Economy Car', 'Family Car', 'Off-Road Car'];

const Add = () => {
  const location = useLocation();
  const isEditMode = location.state?.mode === 'edit';

  
  const editData_from_state = location.state?.data || {};
  const [editData,setEditData] = useState(editData_from_state);

  console.log('editData',editData)
  const { register, handleSubmit, setValue, control, reset, formState: { errors } } = useForm({
    defaultValues: {
      brand: editData.brand || '',
      model: editData.model || '',
      carNumber: editData.car_number || '',
      year: editData.year || '',
      color: editData.color || '',
      vehicleType: editData.car_type || '',
      bodyType: editData.body_type || '',
      transmission: editData.transmission || '',
      fuelType: editData.fuel_type || '',
      seatingCapacity: editData.seats || '',
      pricePerDay: editData.price_per_day || '',
      pricePerKm: editData.price_per_km || '',
      freeKm: editData.current_detail?.location_free_km || '',
      features: (editData.features ? editData.features.split(',').map(f => f.trim()).filter(Boolean) : []),
      locationId: editData?.current_detail?.location_id || '',
      nextAvailable: editData.current_detail?.next_available ? new Date(editData.current_detail.next_available).toISOString().slice(0,16) : '',
      locationPricePerDay: editData.current_detail?.location_price_per_day || '',
      locationPricePerKm: editData.current_detail?.location_price_per_km || '',
      locationFreeKm: editData.current_detail?.location_free_km || '',
      tag: editData.tag || [],
    },
  });
  const navigate = useNavigate();

  const [tagsSelected, setTagsSelected] = useState([]);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState([]);
  const [customFeatures, setCustomFeatures] = useState([]);
  const [newFeatureInput, setNewFeatureInput] = useState('');
  const [showFeatureInput, setShowFeatureInput] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [secondaryImages, setSecondaryImages] = useState([]);
  const [fuelOptions, setFuelOptions] = useState([]);
  const [carTypeOptions, setCarTypeOptions] = useState([]);
  const [transmissionOptions, setTransmissionOptions] = useState([]);
  const [bodyTypeOptions, setBodyTypeOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSecondaryImageUpload = (file) => {
    if (file && secondaryImages.length < 3) {
      setSecondaryImages((prev) => [...prev, file]);
    }
  };

  const removeSecondaryImage = (index) => {
    if(isEditMode){
      handleDeleteImage(secondaryImages[index])
    }
    setSecondaryImages((prev) => prev.filter((_, i) => i !== index));
  };
  useEffect(() => {
    
    const fetchCar = async () => {
      try {
        setLoading(true)
        const res = await getCarDetail(editData_from_state?.id);
        console.log("Car Detail API Response:", res?.data?.data);
        setEditData(res?.data?.data)
        setLoading(false)
       
      } catch (error) {
        console.error("Error fetching car detail:", error);
      }
    };

    if (editData_from_state.id) fetchCar();
  }, [editData_from_state?.id]);

  const onSubmit = async (data) => {
    
    try {
      setLoading(true);
      
      const featuresArray = Array.isArray(data.features)
        ? data.features.filter(Boolean)
        : [];
      
      // Helper function to find name from ID
      const findNameById = (id, options) => {
        const option = options.find(opt => opt.id === id);
        return option ? option.name : id;
      };
      
      // Create car payload without cover image URL initially
      const payload = {
        brand: data.brand,
        model: data.model,
        car_number: data.carNumber,
        year: data.year ? Number(data.year) : undefined,
        color: data.color,
        car_type: findNameById(data.vehicleType, carTypeOptions),
        body_type: findNameById(data.bodyType, bodyTypeOptions),
        transmission: findNameById(data.transmission, transmissionOptions),
        fuel_type: findNameById(data.fuelType, fuelOptions),
        seats: data.seatingCapacity ? Number(data.seatingCapacity) : undefined,
        price_per_day: data.pricePerDay ? Number(data.pricePerDay) : undefined,
        price_per_km: data.pricePerKm ? Number(data.pricePerKm) : undefined,
        free_km: data.freeKm ? Number(data.freeKm) : undefined,
        features: featuresArray.join(', '),
        location_id: data.locationId ? Number(data.locationId) : undefined,
        next_available: data.nextAvailable
          ? new Date(data.nextAvailable).toISOString()
          : undefined,
      };

      // Create car first
      const res = isEditMode && editData?.id
        ? await updateCar(editData.id, payload)
        : await createCar(payload);
      
      // Get car ID from response (using car_id field)
      const carId = res?.data?.data?.car_id || editData?.id;
      
      if (carId) {
        // Upload cover image after car creation/update
        if (coverImage) {
          try {
            const coverImageResponse = await uploadCoverImage(coverImage, carId);
            const newCoverImageUrl = coverImageResponse.data.data.image_url;
            setCoverImageUrl(newCoverImageUrl);
          } catch (error) {
            console.error('Failed to upload cover image:', error);
            ToastMsg(isEditMode ? 'Car updated but failed to upload cover image' : 'Car created but failed to upload cover image', 'warning');
          }
        }
        
        // Upload all secondary images as car_other type
        if (secondaryImages.length > 0) {
          try {
            // Filter out existing URLs and only upload new files
            const newImageFiles = secondaryImages.filter(img => img instanceof File);
            if (newImageFiles.length > 0) {
              console.log(`Uploading ${newImageFiles.length} new secondary images for car ID: ${carId}`);
              const uploadPromises = newImageFiles.map((imageFile, index) => {
                console.log(`Uploading secondary image ${index + 1}:`, imageFile.name);
                return uploadSecondaryImage(imageFile, carId);
              });
              const results = await Promise.all(uploadPromises);
              console.log('All new secondary images uploaded successfully:', results.length);
            }
          } catch (error) {
            console.error('Failed to upload secondary images:', error);
            ToastMsg(isEditMode ? 'Car updated but failed to upload some secondary images' : 'Car created but failed to upload some secondary images', 'warning');
          }
        }
        
        // Show success message and redirect only after all operations complete
        ToastMsg(res?.data?.message || (isEditMode ? 'Car updated successfully' : 'Car created successfully'), 'success');
        reset();
        navigate(isEditMode ? -1 : '/car-listings'); // Redirect based on mode
      } else {
        // If no car ID, show success and go back
        ToastMsg(res?.data?.message || (isEditMode ? 'Car updated successfully' : 'Car created successfully'), 'success');
        reset();
        navigate(-1);
      }
    } catch (e) {
      ToastMsg(e?.response?.data?.message || (isEditMode ? 'Failed to update car' : 'Failed to create car'), 'error');
    }
    finally {
      setLoading(false);
    }
  };

  const inputClass = `block w-full rounded-md bg-[#1E1E1E] px-3 py-4 text-sm  
  placeholder-[#454545] outline-none border border-[#333] focus:border-[#F3B753]`;

  const labelClass = `absolute -top-2 left-2 inline-block rounded-lg bg-[#161616] px-1 text-xs font-medium text-[#454545]`;

  const toggleTag = (tag) => {
    if (tempSelected.includes(tag)) {
      setTempSelected(tempSelected.filter(t => t !== tag));
    } else if (tempSelected.length < 2) {
      setTempSelected([...tempSelected, tag]);
    }
  };

  const applySelection = () => {
    setTagsSelected(tempSelected);
    setValue('tag', tempSelected);
    setTagDropdownOpen(false);
  };

  const cancelSelection = () => {
    setTempSelected(tagsSelected);
    setTagDropdownOpen(false);
  };

  const removeTag = (tag) => {
    const updated = tagsSelected.filter(t => t !== tag);
    setTagsSelected(updated);
    setTempSelected(updated);
    setValue('tag', updated);
  };
  useEffect(() => {
    
    if (editData && Object.keys(editData).length > 0) {
      // Helper function to find ID from name
   
 
      // Reset form with edit data - using IDs for dropdowns
      reset({
        brand: editData.brand || '',
        model: editData.model || '',
        carNumber: editData.car_number || '',
        year: editData.year || '',
        color: editData.color || '',
        vehicleType: editData.car_type,
        bodyType: editData.body_type,
        transmission:editData.transmission,
        fuelType: editData.fuel_type,
        seatingCapacity: editData.seats || '',
        pricePerDay: editData.price_per_day || '',
        pricePerKm: editData.price_per_km || '',
        freeKm: editData.current_detail?.location_free_km || '',
        features: (editData.features ? editData.features.split(',').map(f => f.trim()).filter(Boolean) : []),
        locationId: editData?.current_detail?.location_id || '',
        nextAvailable: editData.current_detail?.next_available ? new Date(editData.current_detail.next_available).toISOString().slice(0,16) : '',
        locationPricePerDay: editData.current_detail?.location_price_per_day || '',
        locationPricePerKm: editData.current_detail?.location_price_per_km || '',
        locationFreeKm: editData.current_detail?.location_free_km || '',
        tag: editData.tag || [],
      });
      
      setTagsSelected(editData.tag || []);
      setTempSelected(editData.tag || []);
      
      // Initialize selected features
      const featuresArray = editData.features ? editData.features.split(',').map(f => f.trim()).filter(Boolean) : [];
      setSelectedFeatures(featuresArray);
      
      // Set available features based on mode
      if (isEditMode && featuresArray.length > 0) {
        // Edit mode: show only features from edit data
        setAvailableFeatures(featuresArray);
      } else {
        // Create mode: show static features
        setAvailableFeatures(['Reverse camera', 'Sunroof', 'GPS', 'USB charger', 'Bluetooth', 'ADAS', 'Ventilated', 'Spare tyre', 'Cruise control']);
      }
      
      // Initialize existing images for edit mode
      if (editData.thumnail_image) {
        setCoverImageUrl(editData.thumnail_image);
      }
      if (editData.other_images && Array.isArray(editData.other_images)) {
        setSecondaryImages(editData.other_images);
      }
    }
  }, [editData, reset, carTypeOptions, bodyTypeOptions, transmissionOptions, fuelOptions, locationOptions]);

  // Initialize available features for create mode
  useEffect(() => {
    if (!isEditMode) {
      setAvailableFeatures(['Reverse camera', 'Sunroof', 'GPS', 'USB charger', 'Bluetooth', 'ADAS', 'Ventilated', 'Spare tyre', 'Cruise control']);
    }
  }, [isEditMode]);

  const handleDeleteImage = async(url) => {
    try {
      const res = await deleteImage(url);
      console.log("res",res)
    } catch (e) {
      console.log("e",e)
    }
  }

  useEffect(() => {
    const fetchCarTypes = async () => {
      try {
        const res = await getCarTypesForFilter();
        const items = Array.isArray(res?.data?.data) ? res.data.data : [];
        setCarTypeOptions(items.filter(Boolean));
      } catch (e) {
        setCarTypeOptions([]);
      }
    };
    const fetchBodyTypes = async () => {
      try {
        const res = await getBodyTypesForFilter();
        const items = Array.isArray(res?.data?.data) ? res.data.data : [];
        setBodyTypeOptions(items.filter(Boolean));
      } catch (e) {
        setBodyTypeOptions([]);
      }
    };
    const fetchTransmissions = async () => {
      try {
        const res = await getTransmissionsForFilter();
        const items = Array.isArray(res?.data?.data) ? res.data.data : [];
        setTransmissionOptions(items.filter(Boolean));
      } catch (e) {
        setTransmissionOptions([]);
      }
    };
    const fetchLocations = async () => {
      try {
        const res = await getOperatingLocations();
        const items = Array.isArray(res?.data?.data) ? res.data.data : [];
        setLocationOptions(items.map((item) => ({ id: item?.id, label: item?.address })).filter(o => o?.id && o?.label));
      } catch (e) {
        setLocationOptions([]);
      }
    };
   
    const fetchFuelTypes = async () => {
      try {
        const res = await getFuelTypesForFilter();
        const items = Array.isArray(res?.data?.data) ? res.data.data : [];
        setFuelOptions(items.filter(Boolean));
      } catch (e) {
        setFuelOptions([]);
      }
    };
    fetchFuelTypes();
    fetchCarTypes();
    fetchBodyTypes();
    fetchTransmissions();
    fetchLocations();
  }, []);


  const renderFloatingInput = (name, label, placeholder, type = 'text', required = true) => (
    <div className="relative mt-3">
      <label htmlFor={name} className={labelClass}>
        {label}
      </label>
      <input
        {...register(name, { 
          required: required ? `${label} is required` : false,
          ...(type === 'number' && { 
            min: { value: 0, message: `${label} must be greater than 0` }
          })
        })}
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        className={`${inputClass} ${errors[name] ? 'border-red-500' : ''}`}
      />
      {errors[name] && (
        <p className="text-red-500 text-xs mt-1">{errors[name].message}</p>
      )}
    </div>
  );

  const renderFloatingSelect = (name, label, options = [], required = true) => (
    <div className="relative mt-3">
      <label htmlFor={name} className={labelClass}>
        {label}
      </label>
      <select
        {...register(name, { 
          required: required ? `${label} is required` : false
        })}
        id={name}
        name={name}
        className={`${inputClass} ${errors[name] ? 'border-red-500' : ''}`}
      >
        <option value="" hidden className="text-[#454545]">
          Select {label}
        </option>
        {options.map(opt => (
          typeof opt === 'string' ? (
            <option key={opt} value={opt}>{opt}</option>
          ) : opt.label ? (
            <option key={opt.id} value={opt.name}>{opt.label}</option>
          ) : (
            <option key={opt.id} value={opt.name}>{opt.name}</option>
          )
        ))}
        
      </select>
      {errors[name] && (
        <p className="text-red-500 text-xs mt-1">{errors[name].message}</p>
      )}
    </div>
  );

  const renderFloatingSelectLocation = (name, label, options = [], required = true) => (
    <div className="relative mt-3">
      <label htmlFor={name} className={labelClass}>
        {label}
      </label>
      <select
        {...register(name, { 
          required: required ? `${label} is required` : false
        })}
        id={name}
        name={name}
        className={`${inputClass} ${errors[name] ? 'border-red-500' : ''}`}
      >
        <option value="" hidden className="text-[#454545]">
          Select {label}
        </option>
        {options.map(opt => (
          typeof opt === 'string' ? (
            <option key={opt} value={opt}>{opt}</option>
          ) : opt.label ? (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          ) : (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          )
        ))}
        
      </select>
      {errors[name] && (
        <p className="text-red-500 text-xs mt-1">{errors[name].message}</p>
      )}
    </div>
  );


  if (loading) return <Loader />;

  return (
    <div className="flex flex-col gap-4 p-8 bg-[#161616] min-h-screen">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-[#F3B753] mb-4 hover:underline"
      >
        <ArrowLeft size={18} />
        Back
      </button>


      <h2 className="text-2xl font-semibold mb-1 pb-4 border-b border-[#333]">
        {isEditMode ? 'Edit Unit' : 'Add Unit'}
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderFloatingInput('brand', 'Brand', 'Enter Brand', 'text', true)}
        {renderFloatingInput('model', 'Model', 'Enter Model', 'text', true)}
        {renderFloatingInput('carNumber', 'Car Number', 'Enter Car Number', 'text', true)}
        {renderFloatingInput('year', 'Year', 'Enter Year', 'number', true)}
        {renderFloatingInput('color', 'Color', 'Enter Color', 'text', true)}
        {renderFloatingSelect('vehicleType', 'Vehicle Type', carTypeOptions, true)}
        {renderFloatingSelect('bodyType', 'Body Type', bodyTypeOptions, true)}
        {renderFloatingSelect('transmission', 'Transmission', transmissionOptions, true)}
        {renderFloatingSelect('fuelType', 'Fuel Type', fuelOptions, true)}
        {renderFloatingSelect('seatingCapacity', 'No. of Seater', ['2', '4', '5', '7'], true)}
        {renderFloatingInput('pricePerKm', 'Price Per KM', 'Enter Price per KM', 'number', false)}
        {renderFloatingInput('freeKm', 'Free KM', 'Enter Free KM', 'number', false)}
        {renderFloatingSelectLocation('locationId', 'Location', locationOptions, true)}
        {renderFloatingInput('nextAvailable', 'Next Available', 'Select Date/Time', 'datetime-local', false)}
       
     
        <div className="col-span-1">
          <label className="text-sm text-[#454545] block mb-1">Cover Image</label>
          {coverImage || coverImageUrl ? (
            <div className="relative border border-dashed border-[#333] rounded-md bg-[#1E1E1E] px-16 py-4 flex items-center gap-4">
              <img
                src={coverImage ? URL.createObjectURL(coverImage) : coverImageUrl}
                alt="cover"
                className="w-[90px] h-[70px] object-cover rounded-md"
              />
              <div className="flex flex-col gap-2 text-[#454545] text-xs">
                <span className='text-[#454545] font-medium'>
                  {coverImage ? `${(coverImage.size / 1024).toFixed(0)} KB` : 'Existing Image'}
                </span>
                <span className="flex items-center font-normal">
                  <CircleCheck size={14} className="mr-1 text-green-500" /> 
                  {coverImage ? 'Ready to upload' : 'Current image'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  if(isEditMode){
                    handleDeleteImage(coverImageUrl)
                  }
                  setCoverImage(null);
                  setCoverImageUrl('');
                  
                }}
                className="ml-auto text-white hover:text-red-400"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ) : (
            <label className="border border-dashed hover:border-[#F3B753] border-[#333] text-[#A0A0A0] bg-[#1E1E1E] rounded-md px-4 py-3 text-center text-sm hover:text-[#F3B753] flex flex-col items-center gap-2 cursor-pointer">
              <Upload size={20} />
              Upload Cover Image
              <p className="text-[10px] text-[#A0A0A0] mt-1">Only support .jpg, .png, .svg and files</p>
              <input
                type="file"
                accept=".jpg,.png,.svg"
                className="hidden"
                onChange={(e) => setCoverImage(e.target.files[0])}
              />
            </label>
          )}
        </div>

        <div className="col-span-1">
          <label className="text-sm text-[#454545] block mb-1">Secondary Images (upto 3 - will be uploaded as car_other type)</label>
          <div className="flex gap-7">
            {secondaryImages.map((img, i) => (
              <div key={i} className="relative w-full h-[88px] rounded-md overflow-hidden">
                <img 
                  src={img instanceof File ? URL.createObjectURL(img) : img} 
                  alt={`secondary-${i}`} 
                  className="w-full h-full object-cover" 
                />
                <button
                  type="button"
                  onClick={() => removeSecondaryImage(i)}
                  className="absolute top-1 right-1 bg-[#454545] rounded-full p-1 hover:bg-black"
                >
                  <X size={14} className="text-white" />
                </button>
              </div>
            ))}

            {secondaryImages.length < 3 &&
              Array.from({ length: 3 - secondaryImages.length }).map((_, i) => (
                <label
                  key={i}
                  className="w-full h-[88px] border border-dashed border-[#333] hover:border-[#F3B753] bg-[#1E1E1E] rounded-md flex items-center justify-center cursor-pointer"
                >
                  <Upload size={20} className="text-gray-500 hover:text-[#F3B753]" />
                  <input
                    type="file"
                    accept=".jpg,.png,.svg"
                    className="hidden"
                    onChange={(e) => {
                      handleSecondaryImageUpload(e.target.files[0]);
                      e.target.value = ''; // Reset input to allow same file selection
                    }}
                  />
                </label>
              ))}
          </div>
          <p className="text-[10px] text-[#A0A0A0] mt-1">Only support .jpg, .png and .svg and files</p>
        </div>

        <div className="relative mt-3">
          <label htmlFor="pricePerDay" className={`${labelClass}`}>
            Price Per Day
          </label>
          <div className="flex items-center">
            <span className="text-[#454545] absolute left-3">€ |</span>
            <input
              {...register('pricePerDay', { 
                required: 'Price Per Day is required',
                min: { value: 0, message: 'Price Per Day must be greater than 0' }
              })}
              id="pricePerDay"
              type="number"
              placeholder="Enter Price"
              className={`${inputClass} pl-8 ${errors.pricePerDay ? 'border-red-500' : ''}`}
            />
          </div>
          {errors.pricePerDay && (
            <p className="text-red-500 text-xs mt-1">{errors.pricePerDay.message}</p>
          )}
        </div>

        {/* Custom Tag Selector */}
        {/* <div className="relative mt-3">
          <label htmlFor="tag" className={labelClass}>
            Select Tag (Upto 2)
          </label>
          <div
            className="w-full bg-[#1E1E1E] border border-[#333] rounded-md px-3 text-sm text-white cursor-pointer"
            onClick={() => setTagDropdownOpen(!tagDropdownOpen)}
          >
            <div className="flex flex-wrap gap-2">
              {tagsSelected.map(tag => (
                <span key={tag} className="bg-[#2E2E2E] text-[#fff] px-3 py-1 my-3 rounded flex items-center gap-2 text-sm">
                  {tag}
                  <X size={14} onClick={(e) => { e.stopPropagation(); removeTag(tag); }} className="cursor-pointer text-[#F3B753]" />
                </span>
              ))}
              {tagsSelected.length === 0 && (
                <span className="text-[#454545] py-4">Select Tag</span>
              )}
            </div>
            <ChevronDown className="absolute right-4 top-5 text-[#454545]" size={20} />
          </div>

          {tagDropdownOpen && (
            <div className="absolute z-10 w-full mt-2 bg-[#2b2b2b] rounded-md shadow-lg border border-[#333] p-4 py-2">
              <ul className="space-y-2 max-h-40 overflow-y-auto">
                {tags.map(tag => (
                  <li
                    key={tag}
                    className="border-b border-[#454545] flex items-center justify-between text-sm text-white cursor-pointer hover:bg-[#3a3a3a] px-2 py-2 rounded"
                    onClick={() => toggleTag(tag)}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={tempSelected.includes(tag)}
                        onChange={() => toggleTag(tag)}
                        icon={<Check size={16} color="#FFFFFF" />}
                        borderColor="#454545"
                        borderRadius={4}
                        style={{
                          backgroundColor: tempSelected.includes(tag) ? '#F3B753' : 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(4px)',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderWidth: '1px',
                          transition: 'background-color 0.2s, border-color 0.2s',
                        }}
                        borderWidth={1}
                        className="cursor-pointer"
                      />
                      {tag}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="flex justify-end gap-4 mt-4">
                <button
                  onClick={cancelSelection}
                  type="button"
                  className="border border-[#F3B753] text-[#F3B753] px-4 py-1 rounded text-sm hover:bg-[#1c1c1c]"
                >
                  Cancel
                </button>
                <button
                  onClick={applySelection}
                  type="button"
                  className="bg-[#F3B753] text-black px-4 py-1 rounded text-sm hover:bg-[#e3a840]"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div> */}

        <div className="col-span-2 mt-4">
          <label className="text-sm font-normal text-[#454545] block mb-2">
            Features
          </label>

          <div className="flex flex-wrap gap-4">
            {[...availableFeatures, ...customFeatures].map((feature, index) => (
              <label key={index} className="flex items-center gap-2 text-base font-normal text-[#f6f6f6]">
                <Controller
                  name={`features[${index}]`}
                  control={control}
                  defaultValue={selectedFeatures.includes(feature)}
                  render={({ field: { onChange, value } }) => (
                    <Checkbox
                      checked={selectedFeatures.includes(feature)}
                      onChange={(checked) => {
                        if (checked) {
                          const newFeatures = [...selectedFeatures, feature];
                          setSelectedFeatures(newFeatures);
                          setValue('features', newFeatures);
                        } else {
                          const newFeatures = selectedFeatures.filter(f => f !== feature);
                          setSelectedFeatures(newFeatures);
                          setValue('features', newFeatures);
                        }
                        onChange(checked ? feature : undefined);
                      }}
                      icon={<Check size={22} color="#FFFFFF" />}
                      borderColor="#454545"
                      borderRadius={4}
                      style={{
                        backgroundColor: selectedFeatures.includes(feature) ? '#F3B753' : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(4px)',
                        width: '24px',
                        height: '24px',
                        padding: '1px 1px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderWidth: '1px',
                        opacity: 1,
                        transition: 'background-color 0.2s, border-color 0.2s',
                      }}
                      borderWidth={1}
                      className="cursor-pointer"
                    />
                  )}
                />
                {feature}
              </label>
            ))}
          </div>

          {/** Input box for new feature */}
          <div className="mt-4 flex items-center gap-4">
            {showFeatureInput && (

              <input
                type="text"
                placeholder="Feature Name"
                value={newFeatureInput}
                onChange={(e) => setNewFeatureInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newFeatureInput.trim()) {
                    const newFeature = newFeatureInput.trim();
                    setCustomFeatures((prev) => [...prev, newFeature]);
                    setNewFeatureInput('');
                    setShowFeatureInput(false);
                  }
                }}
                className="bg-[#1E1E1E] placeholder-[#454545] text-white text-sm px-4 py-2 rounded-md border border-[#333] focus:outline-none focus:border-[#F3B753] w-1/4"
              />

            )}

            <button
              type="button"
              onClick={() => setShowFeatureInput(true)}
              className="text-[#F3B753] font-normal text-sm hover:underline "
            >
              + Add More Features
            </button>
          </div>
        </div>


        <div className="col-span-2 mt-8 flex gap-4 justify-end">
          <button   onClick={() => navigate(-1)} type="button" className="border border-[#F3B753] text-[#f3b753] px-6 py-2 rounded text-sm hover:bg-[#2d2d2d]">
            Cancel
          </button>
          <button type="submit" className="bg-[#F3B753] text-black px-6 py-2 rounded text-sm hover:bg-[#e3a840]">
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default Add; 